import { Link } from "react-router-dom";
import { ArrowRight, Download, Check } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";
import Reveal from "@/components/site/Reveal";
import { SCORECARD_PDF, SCORECARD_FILENAME } from "@/lib/constants";

function reDownload() {
  const a = document.createElement("a");
  a.href = SCORECARD_PDF;
  a.download = SCORECARD_FILENAME;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function ThankYou() {
  usePageTitle("Thank You");
  return (
    <div data-testid="page-thank-you" className="bg-ivory min-h-screen">
      <section className="pt-40 lg:pt-48 pb-24 lg:pb-32 grain">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <span className="block w-2 h-2 rounded-full bg-sage" />
                <p className="font-ui uppercase tracking-[0.22em] text-[13px] font-semibold text-sage">
                  Your Scorecard is Downloading
                </p>
              </div>

              <h1 className="font-serif text-4xl md:text-5xl lg:text-[3.75rem] text-navy leading-[1.08]">
                Thank you —{" "}
                <em className="font-serif italic text-sage">your scorecard is on its way.</em>
              </h1>

              <p className="font-sans text-base md:text-lg text-charcoal/70 mt-8 leading-[1.8] max-w-2xl">
                The Revenue Leak Audit Scorecard has started downloading. If the download didn't begin automatically,
                you can{" "}
                <button onClick={reDownload} data-testid="thankyou-redownload" className="font-semibold text-navy underline underline-offset-4 decoration-gold/70 hover:decoration-navy transition-colors">
                  download it again here
                </button>
                .
              </p>

              <span className="gold-rule mt-12 block" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-28 lg:pb-36 bg-cream pt-20 lg:pt-24">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <Reveal as="div" className="lg:col-span-7">
              <p className="overline mb-5">A Brief Note</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.5rem] text-navy leading-[1.15]">
                The Revenue Leak Audit goes{" "}
                <em className="font-serif italic text-sage">a layer deeper.</em>
              </h2>
              <p className="font-sans text-charcoal/75 text-[16px] md:text-[17px] leading-[1.85] mt-7 max-w-2xl">
                The scorecard helps you see where friction may be quietly limiting your business.
                The full Revenue Leak Audit is the next step — a focused review of your actual listing,
                positioning, pricing, and guest experience to surface the 3–5 things most likely costing you bookings.
              </p>
              <p className="font-sans text-charcoal/70 text-[15.5px] leading-[1.85] mt-5 max-w-2xl">
                It's the calm, considered way to translate awareness into action.
              </p>

              <div className="mt-12 flex flex-col sm:flex-row gap-4">
                <Link to="/services" data-testid="thankyou-cta-services" className="btn-primary">
                  Explore the Revenue Leak Audit <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact?type=audit" data-testid="thankyou-cta-contact" className="btn-outline">
                  Start a Conversation
                </Link>
              </div>
            </Reveal>

            <Reveal as="aside" delay={120} className="lg:col-span-5">
              <div className="border-l-2 border-gold pl-7">
                <p className="overline mb-5">While You Review</p>
                <ul className="space-y-4">
                  <li className="flex gap-3 font-sans text-[15.5px] text-charcoal/80 leading-snug">
                    <Check className="w-4 h-4 text-sage shrink-0 mt-1" strokeWidth={1.8} />
                    <span>Move through each section honestly — green, yellow, and red are all useful signals.</span>
                  </li>
                  <li className="flex gap-3 font-sans text-[15.5px] text-charcoal/80 leading-snug">
                    <Check className="w-4 h-4 text-sage shrink-0 mt-1" strokeWidth={1.8} />
                    <span>Note the patterns. The pattern matters more than any single item.</span>
                  </li>
                  <li className="flex gap-3 font-sans text-[15.5px] text-charcoal/80 leading-snug">
                    <Check className="w-4 h-4 text-sage shrink-0 mt-1" strokeWidth={1.8} />
                    <span>If something surfaces clearly, that's exactly what the scorecard is for.</span>
                  </li>
                </ul>

                <button
                  onClick={reDownload}
                  data-testid="thankyou-sidebar-redownload"
                  className="editorial-link mt-10 inline-flex items-center"
                >
                  Download the Scorecard Again <Download className="w-3.5 h-3.5 ml-1.5" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
